import { memo, type FormHTMLAttributes, type ReactNode } from 'react';
import { tv } from 'tailwind-variants';
import { ENTITY_MODAL_FORM_SHELL_CLASS } from './entityUnifiedModalStyles.js';

const formShell = tv({
  base: `${ENTITY_MODAL_FORM_SHELL_CLASS} w-full`,
});

export interface FormShellProps extends Omit<FormHTMLAttributes<HTMLFormElement>, 'children'> {
  readonly children: ReactNode;
  /** Render as `<form>` when submitting; default is a constrained content wrapper. */
  readonly as?: 'div' | 'form';
  readonly testId?: string;
}

/**
 * Canonical form content width shell (Wave 3 / G21).
 * Width measure is CSS SSOT: standalone forms cap at --container-form (720px);
 * inside `.kiosk-unified-modal` the shell is full track (see `components.css`).
 */
export const FormShell = memo<FormShellProps>(
  ({ children, as = 'div', className, testId = 'form-shell', ...rest }) => {
    const classNames = formShell({ className });
    if (as === 'form') {
      return (
        <form className={classNames} data-testid={testId} {...rest}>
          {children}
        </form>
      );
    }
    return (
      <div className={classNames} data-testid={testId}>
        {children}
      </div>
    );
  },
);

FormShell.displayName = 'FormShell';
